import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { X, Plus, Loader2 } from "lucide-react";
import { eventPreferencesApi, brandSubscriptionsApi, useGetOrganizationNotificationSettings } from "@/resources/notification";
import type { EventPreferences } from "@/resources/notification/event-preferences.api";
import { AddBrandDialog } from "./AddBrandDialog";
import { TruncatedText } from "@/components/ui/truncated-text";
import { handleApiError } from "@/utils";
import { toast } from "sonner";
import { DEFAULT_PREFS } from "./notifications/constants";
import { RemoveAllDialog } from "./notifications/RemoveAllDialog";

export function NotificationsTab() {
  const { data: savedPrefs, isLoading: prefsLoading } =
    eventPreferencesApi.useGetEventPreferences();
  const { mutateAsync: updatePrefs, isPending: savePending } =
    eventPreferencesApi.useUpdateEventPreferences();

  const { data: orgSettings } = useGetOrganizationNotificationSettings();

  const { data: subsData, isLoading: subsLoading } =
    brandSubscriptionsApi.useGetBrandSubscriptions();
  const { mutate: unsubscribeBrand } = brandSubscriptionsApi.useUnsubscribeBrand();
  const { mutate: unsubscribeAll, isPending: removeAllPending } =
    brandSubscriptionsApi.useUnsubscribeAllBrands();

  const [localPrefs, setLocalPrefs] = useState<EventPreferences>(DEFAULT_PREFS);
  const [addBrandOpen, setAddBrandOpen] = useState(false);
  const [removeAllOpen, setRemoveAllOpen] = useState(false);

  useEffect(() => {
    if (savedPrefs && !savePending) {
      setLocalPrefs(savedPrefs);
    }
  }, [savedPrefs, savePending]);

  const activeCount = [
    localPrefs.signal,
    localPrefs.rank_change,
    localPrefs.landscape_complete,
    localPrefs.ab_test_results,
  ].filter(Boolean).length;

  const subscriptions = subsData?.subscriptions ?? [];
  const subCount = subsData?.count ?? 0;

  const handleToggle = <K extends keyof EventPreferences>(key: K, value: boolean) => {
    setLocalPrefs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      await updatePrefs(localPrefs);
      toast.success("Event preferences saved.");
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleRemoveAll = () => {
    unsubscribeAll(undefined, {
      onSuccess: () => {
        setRemoveAllOpen(false);
        toast.success("All brand subscriptions removed.");
      },
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
      <Card className="flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">Brand Notifications</CardTitle>
              {subsLoading ? (
                <Skeleton className="h-4 w-28 mt-1" />
              ) : (
                <CardDescription>{subCount} brand{subCount !== 1 ? "s" : ""} enabled</CardDescription>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setRemoveAllOpen(true)}
                disabled={subscriptions.length === 0}
              >
                Remove All
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => setAddBrandOpen(true)}
              >
                <Plus className="h-3.5 w-3.5" />
                Add Brand
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1">
          {subsLoading && (
            <div className="space-y-2">
              {[1, 2, 3].map((n) => (
                <div key={n} className="flex items-center gap-3 p-3 rounded-lg border border-border">
                  <Skeleton className="h-9 w-9 rounded-full shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="h-6 w-6 rounded" />
                </div>
              ))}
            </div>
          )}

          {!subsLoading && subscriptions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed border-border rounded-lg">
              <p className="text-sm text-muted-foreground">No brands subscribed.</p>
              <p className="text-xs text-muted-foreground mt-1">Click Add Brand to start.</p>
            </div>
          )}

          {!subsLoading && subscriptions.length > 0 && (
            <div className="space-y-2">
              {subscriptions.map((sub) => (
                <div
                  key={sub.brand_id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border"
                >
                  <div className="h-9 w-9 rounded-full bg-primary-tint-soft flex items-center justify-center text-xs font-semibold text-primary shrink-0">
                    {sub.brand_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      <TruncatedText>{sub.brand_name}</TruncatedText>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {activeCount} alert type{activeCount !== 1 ? "s" : ""} active
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive shrink-0"
                    onClick={() => unsubscribeBrand(sub.brand_id)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="flex flex-col">
        <CardHeader>
          <CardTitle className="text-base">Event Preferences</CardTitle>
          <CardDescription>Choose which events trigger notifications</CardDescription>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col">
          {prefsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="flex items-center justify-between py-1">
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                  <Skeleton className="h-6 w-10 rounded-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex-1 space-y-0">
              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-foreground">Signal</p>
                  <p className="text-xs text-muted-foreground">New brand signals validated</p>
                </div>
                <Switch
                  checked={localPrefs.signal}
                  onCheckedChange={(v) => handleToggle("signal", v)}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-foreground">Rank Change Alerts</p>
                  <p className="text-xs text-muted-foreground">
                    {orgSettings?.rank_change_threshold != null
                      ? `Keywords move ±${orgSettings.rank_change_threshold} ranks`
                      : "Keywords move past threshold"}
                  </p>
                </div>
                <Switch
                  checked={localPrefs.rank_change}
                  onCheckedChange={(v) => handleToggle("rank_change", v)}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-foreground">Landscape</p>
                  <p className="text-xs text-muted-foreground">Scheduled landscape finishes</p>
                </div>
                <Switch
                  checked={localPrefs.landscape_complete}
                  onCheckedChange={(v) => handleToggle("landscape_complete", v)}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-foreground">A/B Test</p>
                  <p className="text-xs text-muted-foreground">Test reaches significance</p>
                </div>

                <Switch
                  checked={localPrefs.ab_test_results}
                  onCheckedChange={(v) => handleToggle("ab_test_results", v)}
                />
              </div>
            </div>
          )}

          <div className="pt-4">
            <Button
              className="w-full"
              onClick={handleSave}
              disabled={savePending || prefsLoading}
            >
              {savePending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </span>
              ) : (
                "Save Settings"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <AddBrandDialog open={addBrandOpen} onOpenChange={setAddBrandOpen} />

      <RemoveAllDialog
        open={removeAllOpen}
        onOpenChange={setRemoveAllOpen}
        count={subCount}
        onConfirm={handleRemoveAll}
        isPending={removeAllPending}
      />
    </div>
  );
}
